import { Button, InputDay, InputWrapper } from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { day, sortBy } from '@vrplatform/utils';
import { Box, Group, Modal } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import type { Reservation } from '../_queries/useLedgerReservationDetailDrawerQuery';

type Props = {
  closeModal: () => void;
  opened: boolean;
  reservation:
    | {
        id: string;
        checkIn: Reservation['checkIn'];
        bookedAt: Reservation['bookedAt'];
        cancelledAt: Reservation['cancelledAt'];
        generalLedgerPostingAt: Reservation['generalLedgerPostingAt'];
      }
    | undefined
    | null;
};

type FormInputs = {
  postingDate: string | null;
};

export const ReservationPostingDateModal = ({
  reservation,
  closeModal: cl,
  opened,
}: Props) => {
  const methods = useForm<FormInputs>({
    values: {
      postingDate: reservation?.generalLedgerPostingAt ?? null,
    },
  });

  const { mutateAsync } = useApiMutation('put', '/reservations/{id}');

  const invalidate = useInvalidateApiQueries(['reservations']);

  const closeModal = () => {
    cl();
    methods.reset({});
  };

  const earliestDate = sortBy(
    [
      reservation?.checkIn,
      reservation?.bookedAt,
      reservation?.cancelledAt,
    ].filter((x) => x),
    undefined,
    'asc'
  )[0];

  const submit = async (data: FormInputs) =>
    reservation?.id &&
    (await mutateAsync({
      params: {
        path: {
          id: reservation.id,
        },
      },
      body: {
        generalLedgerPostingAt: data.postingDate ?? null,
      } as any,
    })
      .then(() => {
        invalidate();
        closeModal();
      })
      .catch((error) =>
        showApiErrorNotification({
          error,
          title: 'Failed to update reservation',
        })
      ));

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={'Overwrite Reservation Posting Date'}
      styles={{
        title: { fontWeight: 500 },
      }}
      size={500}
      centered
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
        })}
      >
        <Controller
          control={methods.control}
          name="postingDate"
          render={({ field, fieldState }) => {
            const v = field.value ? day(field.value).toDate() : null;

            return (
              <InputWrapper
                label="Posting date"
                description="Set a Posting Date to override revenue recognition rules for all financials and fees. Remove the Posting Date to restore pro-rata, check-in or check-out recognition."
                error={fieldState.error?.message}
                styles={(theme) => ({
                  description: {
                    lineHeight: theme.lineHeights.sm,
                    marginTop: theme.spacing.xs,
                  },
                })}
              >
                <InputDay
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  required
                  value={v}
                  error={!!fieldState.error?.message}
                  placeholder={'Select date'}
                  clearable
                  popoverProps={{
                    position: 'bottom-end',
                    withinPortal: true,
                  }}
                  minDate={
                    earliestDate && day(earliestDate).isValid()
                      ? day(earliestDate).toDate()
                      : undefined
                  }
                />
              </InputWrapper>
            );
          }}
        />

        <Group justify="right" mt="xs">
          <Button
            onClick={closeModal}
            disabled={methods.formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={methods.handleSubmit(submit)}
            loading={methods.formState.isSubmitting}
          >
            Submit
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
